import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CLAUSES_BY_SLUG } from "@/data/clauses";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  FileText,
  MessageCircleQuestion,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/clauses/$slug")({
  head: ({ params }) => {
    const c = CLAUSES_BY_SLUG[params.slug];
    if (!c) {
      return { meta: [{ title: "Clause not found — legaldecoder" }] };
    }
    const url = `https://legaldecoder.io/clauses/${params.slug}`;
    return {
      meta: [
        { title: c.metaTitle },
        { name: "description", content: c.metaDescription },
        { property: "og:title", content: c.metaTitle },
        { property: "og:description", content: c.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: ClausePage,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Clause not found</h1>
        <Link to="/clauses" className="mt-4 inline-block text-primary-glow hover:underline">
          Back to clause library
        </Link>
      </div>
    </div>
  ),
});

function ClausePage() {
  const { slug } = Route.useParams();
  const clause = CLAUSES_BY_SLUG[slug];
  if (!clause) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Clause not found</h1>
          <Link to="/clauses" className="mt-4 inline-block text-primary-glow hover:underline">
            Back to clause library
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <Link
              to="/clauses"
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Clause library
            </Link>
            <span className="inline-block rounded-full border border-border bg-card/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {clause.category === "lease" ? "Lease & Rental" : "Work, NDA & Freelance"}
            </span>
            <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">{clause.title}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{clause.intro}</p>
          </div>
        </section>

        <article className="mx-auto max-w-3xl space-y-10 px-4 py-12 sm:px-6 lg:px-8">
          <Section title="What it is">
            <p>{clause.whatItIs}</p>
          </Section>

          <Section title="Why it matters">
            <p>{clause.whyItMatters}</p>
          </Section>

          <Section title="Sample clause language" icon={<FileText className="h-5 w-5" />}>
            <pre className="whitespace-pre-wrap rounded-xl border border-border bg-muted/30 p-4 font-mono text-sm leading-relaxed text-foreground/90">
              {clause.sampleLanguage}
            </pre>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong className="text-foreground">What it really means:</strong>{" "}
              {clause.sampleExplanation}
            </p>
          </Section>

          <div className="grid gap-6 sm:grid-cols-2">
            <FlagsCard
              tone="danger"
              icon={<AlertTriangle className="h-5 w-5" />}
              title="Red flags"
              items={clause.redFlags}
            />
            <FlagsCard
              tone="success"
              icon={<CheckCircle2 className="h-5 w-5" />}
              title="Fair / acceptable"
              items={clause.greenFlags}
            />
          </div>

          <Section title="How to negotiate" icon={<Lightbulb className="h-5 w-5" />}>
            <ul className="space-y-2">
              {clause.negotiationTips.map((tip) => (
                <li key={tip} className="flex gap-2 text-base">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-glow" />
                  {tip}
                </li>
              ))}
            </ul>
          </Section>

          <Section
            title="Frequently asked questions"
            icon={<MessageCircleQuestion className="h-5 w-5" />}
          >
            <Accordion type="single" collapsible className="w-full">
              {clause.faq.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger>{f.q}</AccordionTrigger>
                  <AccordionContent>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Section>

          <div className="rounded-2xl border border-primary-glow/30 bg-gradient-to-br from-primary/10 to-primary-glow/5 p-6">
            <h2 className="text-xl font-bold">Decode this clause in your contract</h2>
            <p className="mt-2 text-muted-foreground">
              Upload your contract and we&apos;ll flag this and every other risky clause — in plain
              English, in seconds.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {clause.relatedTools.map((t) => (
                <Link
                  key={t.slug}
                  to="/tools/$slug"
                  params={{ slug: t.slug }}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary-glow px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary"
                >
                  Open {t.name} <ArrowRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
          </div>

          <Section title="Related clauses">
            <ul className="grid gap-3 sm:grid-cols-2">
              {clause.relatedClauses.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/clauses/$slug"
                    params={{ slug: r.slug }}
                    className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-card hover:border-primary-glow/40"
                  >
                    <span className="text-sm font-semibold group-hover:text-primary-glow">
                      {r.name}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary-glow" />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <p className="border-t border-border pt-6 text-center text-xs uppercase tracking-wider text-muted-foreground">
            Not legal advice. For informational purposes only.
          </p>
        </article>

        <Footer />
      </main>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-xl font-bold">
        {icon ? <span className="text-primary-glow">{icon}</span> : null}
        {title}
      </h2>
      <div className="space-y-3 text-base leading-relaxed text-foreground/90">{children}</div>
    </section>
  );
}

function FlagsCard({
  tone,
  icon,
  title,
  items,
}: {
  tone: "danger" | "success";
  icon: React.ReactNode;
  title: string;
  items: string[];
}) {
  const toneCls =
    tone === "danger" ? "border-destructive/30 bg-destructive/5" : "border-success/30 bg-success/5";
  const iconCls = tone === "danger" ? "text-destructive" : "text-success";
  return (
    <div className={`rounded-2xl border p-5 ${toneCls}`}>
      <h3
        className={`mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider ${iconCls}`}
      >
        {icon}
        {title}
      </h3>
      <ul className="space-y-2 text-sm">
        {items.map((it) => (
          <li key={it} className="flex gap-2">
            <span
              className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${tone === "danger" ? "bg-destructive" : "bg-success"}`}
            />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
